import { useCallback, useEffect, useRef, useState } from 'react';

import {
    MicrostoreColumnsFilterInput,
    MicrostoreElementState,
    useGetMicrostoreColumnsPaginatedQuery,
} from 'data/__generated__';

import { DataTable } from 'components/DataTable';
import type { QuantityGenerationColumn } from 'pages/MicrostoreColumnGroups/Popups/QuantityGenerationPopup';

import { columnsListColumns, type ColumnRow } from '../constants/columnsListColumns';

export const ColumnsList = ({
    onSelectedColumnsChange,
}: {
    onSelectedColumnsChange?: (columns: QuantityGenerationColumn[]) => void;
}) => {
    const defaultFilters: MicrostoreColumnsFilterInput = {
        states: [MicrostoreElementState.Regular],
    };

    const {
        loading: columnsLoading,
        data: columnsData,
        error: columnsError,
        fetchMore,
        refetch,
    } = useGetMicrostoreColumnsPaginatedQuery({
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: defaultFilters,
        },
    });

    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    // Selected rows can scroll out of `data` (infinite scroll / refetch on filter change), so we keep a
    // running map of every column we have seen to resolve names for keys no longer in the current page.
    const rowsByIdRef = useRef<Map<string, ColumnRow>>(new Map());

    if (columnsError) {
        throw new Error('Une erreur est survenue lors de la récupération des colonnes');
    }

    const hasMore = columnsData?.microstoreColumnsFilter.hasMore ?? false;
    const microstoreColumns = columnsData?.microstoreColumnsFilter.microstoreColumns ?? [];

    for (const column of microstoreColumns) {
        rowsByIdRef.current.set(column._id, column);
    }

    useEffect(() => {
        if (!onSelectedColumnsChange) {
            return;
        }
        const selectedColumns = selectedKeys
            .map((id) => rowsByIdRef.current.get(id))
            .filter((column): column is ColumnRow => Boolean(column))
            .map(({ _id, name, siteName }) => ({ _id, name, siteName }));
        onSelectedColumnsChange(selectedColumns);
    }, [selectedKeys, onSelectedColumnsChange, microstoreColumns]);

    const handleSelectionChange = useCallback((keys: string[]) => {
        setSelectedKeys(keys);
    }, []);

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<ColumnRow, MicrostoreColumnsFilterInput>
                columns={columnsListColumns}
                data={microstoreColumns}
                filterMode="server"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/microstore-column/${row._id}`}
                hasMore={hasMore}
                isLoading={columnsLoading}
                enableSelection
                selectedKeys={selectedKeys}
                onSelectionChange={handleSelectionChange}
                refetch={async (vars) => {
                    await refetch({ ...vars });
                }}
                fetchMore={async (opts) => {
                    await fetchMore({ variables: opts.variables });
                }}
                defaultFilters={defaultFilters}
                searchPlaceholder="Nom de la machine"
                searchFilterKey="searchValue"
            />
        </div>
    );
};
